import { useConfig } from '../context/ConfigContext';
import { formatMoney, formatAmount, roundMoney } from '../utils/currencyUtil';
import { POS_PAYMENT_NOTE, POS_VAT_MODE_INCLUSIVE, POS_CASH_METHOD } from '../constants/posConst';
import { posPaymentMethod } from '../utils/posUtil';
import PosReceiptItemRows from './PosReceiptItemRows';
import '../css/Pos.css';

/**
 * One order, laid out as a receipt.
 *
 * Shared by three places — the confirmation before charging, the receipt
 * shown once an order is recorded, and reading one back from Sales — so a
 * receipt looks identical whichever way you arrive at it. Staff comparing the
 * screen against a paper slip should not have to translate between layouts.
 *
 * The lines are a TABLE rather than a list: an order holds the same product at
 * two sizes often enough that "2x Chippy" on its own is ambiguous about which
 * ₱40 it refers to. Columns give quantity and unit price their own headed
 * place, and put every figure in one right-aligned money column instead of
 * flush against the panel edge. The totals live in the table's own footer so
 * SUBTOTAL, VAT and TOTAL land in that same column.
 *
 * CASH and CHANGE only appear on a cash sale with an amount entered. Printing
 * "CHANGE ₱0" on a GCash sale would be stating something untrue about the
 * transaction.
 *
 * VAT, when the client charges it, prints between the items and the total:
 * SUBTOTAL, then the split. Every figure comes off the ORDER, never off the
 * club's live settings — see vatCharged below.
 *
 * @param {{items: object[], total: number, subtotal?: number, vat?: number, vatNet?: number, vatRate?: number, vatMode?: string, paymentMethod?: string, createdAt?: string, cashier?: string, cashReceived?: number, change?: number}} props.order
 * @param {object} props
 * @param {string} props.courtName
 * @param {string} [props.reference] - the order's id, once it has one
 * @param {boolean} [props.showPaymentNote] - the "paid first" warning; only
 *   before charging, where it is an instruction rather than history
 * @param {React.ReactNode} [props.beforeNote] - controls the confirm step
 *   places under the receipt card and above the payment note (the delivery
 *   fee). Outside the card, because they are not part of the document.
 * @returns {JSX.Element}
 */
function PosReceipt({ order, courtName, reference = null, showPaymentNote = false, beforeNote = null }) {
  const { config } = useConfig();
  /** One place, so every figure on the receipt matches. */
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (value) => formatMoney(value, config);

  const items = order?.items || [];
  const when = order?.createdAt ? new Date(order.createdAt) : new Date();

  // THE LABEL STORED ON THE ORDER WINS. A club can rename a payment method or
  // stop offering it, and a receipt has to keep saying what the customer
  // actually paid with — resolving the key against today's list would rewrite
  // history. The lookup is only the fallback, for orders taken before the
  // label was captured.
  const method = order?.paymentLabel
    ? { key: order.paymentMethod, label: order.paymentLabel, icon: '' }
    : posPaymentMethod(order?.paymentMethod);
  // Orders recorded before payment methods existed carry no method at all, so
  // an entered cash amount is still what decides these rows for them.
  const hasCash = (!order?.paymentMethod || order.paymentMethod === POS_CASH_METHOD)
    && Number.isFinite(Number(order?.cashReceived))
    && Number(order?.cashReceived) > 0;

  // Whether THIS order carried tax, decided by what it actually stored. A
  // positive figure rather than the presence of a mode, so an order recorded
  // with a mode but a zero charge does not print an empty VAT line.
  const vatCharged = Number(order?.vat) > 0;
  const isInclusive = order?.vatMode === POS_VAT_MODE_INCLUSIVE;
  // The item cost as a share of the subtotal — the "(88%)". Recomputed from
  // the order's OWN figures rather than stored, so it is always consistent
  // with the two amounts printed beside it. Null when the subtotal is missing
  // or zero, where the share would be meaningless; the label simply drops it.
  const subtotal = Number(order?.subtotal) || 0;
  const netShare = subtotal > 0 ? roundMoney(((Number(order?.vatNet) || 0) / subtotal) * 100) : null;
  // A delivery fee charged on top of the goods. Read off the order, like VAT.
  const deliveryFee = Number(order?.deliveryFee) > 0 ? Number(order.deliveryFee) : 0;
  // The goods alone, for the SUBTOTAL line an order with a fee but no VAT
  // needs — older orders may lack `subtotal`, so it is summed from the lines.
  const goodsSubtotal = order?.subtotal ?? items.reduce((sum, item) => sum + (Number(item.lineTotal) || 0), 0);

  return (
    <div className="pos-receipt">
      <div className="pos-receipt-card">
        <p className="pos-receipt-title">RECEIPT</p>
        <p className="pos-receipt-shop">{courtName || 'Point of Sale'}</p>

        <table className="pos-receipt-table">
          <thead>
            <tr>
              <th scope="col">Product</th>
              <th scope="col" className="num">Qty.</th>
              <th scope="col" className="num">Unit Price</th>
              <th scope="col" className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              // Index in the key: one order can legitimately hold the same
              // product twice, so productId alone would collide. A line with
              // paid options or extras is broken into several rows, each
              // priced in the columns — see PosReceiptItemRows.
              <PosReceiptItemRows key={`${item.productId || item.name}-${index}`} item={item} />
            ))}
          </tbody>
          <tfoot>
            {/* Printed ONLY when tax was actually charged, and read from the
                ORDER rather than from the club's current settings — an
                administrator can change the rate at any time, and a receipt
                that restated itself afterwards would stop matching the paper
                slip the customer is holding. An order taken with no VAT has
                none of these fields, so its footer is the total alone. */}
            {vatCharged && (
              <>
                <tr>
                  <th scope="row" colSpan={3}>SUBTOTAL</th>
                  <td className="num">{peso(order.subtotal)}</td>
                </tr>
                {/* Inclusive: what the subtotal was MADE OF. Exclusive: the tax
                    is not part of the subtotal, so there is no item-cost line
                    to draw — the subtotal above already is the item cost. */}
                {isInclusive && (
                  <tr className="pos-receipt-part">
                    <th scope="row" colSpan={3}>
                      ITEM COST{netShare === null ? '' : ` (${formatAmount(netShare)}%)`}
                    </th>
                    <td className="num">{peso(order.vatNet)}</td>
                  </tr>
                )}
                <tr className={isInclusive ? 'pos-receipt-part' : undefined}>
                  <th scope="row" colSpan={3}>VAT{order.vatRate ? ` (${formatAmount(order.vatRate)}%)` : ''}</th>
                  <td className="num">{peso(order.vat)}</td>
                </tr>
              </>
            )}

            {/* With no VAT the SUBTOTAL above was not printed, so the goods
                figure is given here to make the fee's addition readable. */}
            {deliveryFee > 0 && (
              <>
                {!vatCharged && (
                  <tr>
                    <th scope="row" colSpan={3}>SUBTOTAL</th>
                    <td className="num">{peso(goodsSubtotal)}</td>
                  </tr>
                )}
                <tr>
                  <th scope="row" colSpan={3}>Delivery fee</th>
                  <td className="num">{peso(deliveryFee)}</td>
                </tr>
              </>
            )}

            <tr className="pos-receipt-total">
              <th scope="row" colSpan={3}>Total amount</th>
              <td className="num">{peso(order?.total)}</td>
            </tr>

            {/* Says which of the two the figures above mean. Without it,
                "SUBTOTAL ₱140 / VAT ₱16.80 / TOTAL ₱140" reads as an
                arithmetic mistake rather than as a tax-inclusive price. Kept
                directly under the total it qualifies. */}
            {vatCharged && (
              <tr className="pos-receipt-vatnote">
                <td colSpan={4}>
                  {isInclusive ? 'VAT inclusive — VAT is part of the total above.' : 'VAT exclusive — VAT was added to the subtotal.'}
                  {deliveryFee > 0 ? ' The delivery fee carries no VAT.' : ''}
                </td>
              </tr>
            )}

            {hasCash && (
              <>
                <tr>
                  <th scope="row" colSpan={3}>Cash</th>
                  <td className="num">{peso(order.cashReceived)}</td>
                </tr>
                <tr>
                  <th scope="row" colSpan={3}>Change</th>
                  <td className="num">{peso(order.change)}</td>
                </tr>
              </>
            )}

            {method && (
              <tr className="pos-receipt-method">
                <th scope="row" colSpan={3}>Paid via</th>
                <td className="num">
                  <span aria-hidden="true">{method.emoji}</span> {method.label}
                </td>
              </tr>
            )}
          </tfoot>
        </table>

        <p className="pos-receipt-thanks">Thank you!</p>

        <p className="pos-receipt-foot">
          {when.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
          {order?.cashier ? ` · ${order.cashier}` : ''}
        </p>
        {reference && <p className="pos-receipt-foot pos-receipt-ref">Ref {reference}</p>}
      </div>

      {/* Outside the card: an instruction to the cashier, not part of the
          document the customer is handed.
          Admin -> Config sets the wording for every client; the constant is
          what a database with no global config yet falls back to. */}
      {beforeNote}
      {showPaymentNote && <p className="pos-receipt-note">{config?.paymentNote || POS_PAYMENT_NOTE}</p>}
    </div>
  );
}

export default PosReceipt;
